import { ref, computed } from 'vue'

// Fallback pour les navigateurs qui ne supportent pas crypto.randomUUID
function generateUUID(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // crypto.randomUUID peut exister mais échouer
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

export interface ToastAction {
  label: string
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'danger'
}

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info' | 'loading'
  title?: string
  message: string
  duration?: number
  showProgress?: boolean
  dismissible?: boolean
  actions?: ToastAction[]
  createdAt: number
  pausedAt?: number
  remainingTime?: number
}

export interface ToastOptions {
  title?: string
  duration?: number
  showProgress?: boolean
  dismissible?: boolean
  actions?: ToastAction[]
}

const toasts = ref<Toast[]>([])
const timers = new Map<string, ReturnType<typeof setTimeout>>()

export function useToast() {
  function show(
    type: Toast['type'],
    message: string,
    options: ToastOptions = {}
  ): string {
    const id = generateUUID()
    const duration = options.duration ?? (type === 'error' ? 8000 : type === 'loading' ? 0 : 5000)
    const showProgress = options.showProgress ?? (duration > 0)

    const toast: Toast = {
      id,
      type,
      message,
      title: options.title,
      duration,
      showProgress,
      dismissible: options.dismissible ?? (type !== 'loading'),
      actions: options.actions,
      createdAt: Date.now(),
      remainingTime: duration
    }

    toasts.value.push(toast)

    if (duration > 0) {
      startTimer(id, duration)
    }

    return id
  }

  function startTimer(id: string, duration: number) {
    const timer = setTimeout(() => {
      dismiss(id)
    }, duration)
    timers.set(id, timer)
  }

  function pauseTimer(id: string) {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }

    const toast = toasts.value.find(t => t.id === id)
    if (toast && toast.duration && toast.duration > 0) {
      const elapsed = Date.now() - toast.createdAt
      toast.remainingTime = Math.max(0, toast.duration - elapsed)
      toast.pausedAt = Date.now()
    }
  }

  function resumeTimer(id: string) {
    const toast = toasts.value.find(t => t.id === id)
    if (toast && toast.remainingTime && toast.remainingTime > 0) {
      toast.createdAt = Date.now() - (toast.duration! - toast.remainingTime)
      toast.pausedAt = undefined
      startTimer(id, toast.remainingTime)
    }
  }

  function success(message: string, options?: ToastOptions) {
    return show('success', message, options)
  }

  function error(message: string, options?: ToastOptions) {
    return show('error', message, { duration: 8000, ...options })
  }

  function warning(message: string, options?: ToastOptions) {
    return show('warning', message, options)
  }

  function info(message: string, options?: ToastOptions) {
    return show('info', message, options)
  }

  function loading(message: string, options?: ToastOptions) {
    return show('loading', message, { duration: 0, dismissible: false, showProgress: false, ...options })
  }

  function update(id: string, updates: Partial<Omit<Toast, 'id' | 'createdAt'>>) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      const toast = toasts.value[index]

      // Si on change le type de loading vers autre chose, on ajoute un timer
      if (toast.type === 'loading' && updates.type && updates.type !== 'loading') {
        const duration = updates.duration ?? 5000
        toast.duration = duration
        toast.remainingTime = duration
        toast.createdAt = Date.now()
        toast.dismissible = updates.dismissible ?? true
        toast.showProgress = updates.showProgress ?? true

        if (duration > 0) {
          startTimer(id, duration)
        }
      }

      Object.assign(toast, updates)
    }
  }

  // Pattern Promise : affiche loading puis success/error automatiquement
  async function promise<T>(
    promiseFn: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((err: unknown) => string)
    },
    options?: ToastOptions
  ): Promise<T> {
    const id = loading(messages.loading, options)

    try {
      const result = await promiseFn
      const successMessage = typeof messages.success === 'function'
        ? messages.success(result)
        : messages.success
      update(id, { type: 'success', message: successMessage })
      return result
    } catch (err) {
      const errorMessage = typeof messages.error === 'function'
        ? messages.error(err)
        : messages.error
      update(id, { type: 'error', message: errorMessage })
      throw err
    }
  }

  function dismiss(id: string) {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }

    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  function clear() {
    timers.forEach(timer => clearTimeout(timer))
    timers.clear()
    toasts.value = []
  }

  // Pour les actions avec undo
  function withUndo(
    message: string,
    undoAction: () => void,
    options?: Omit<ToastOptions, 'actions'>
  ) {
    return success(message, {
      ...options,
      duration: 8000,
      actions: [
        {
          label: 'Annuler',
          onClick: undoAction,
          variant: 'secondary'
        }
      ]
    })
  }

  return {
    toasts: computed(() => toasts.value),
    show,
    success,
    error,
    warning,
    info,
    loading,
    update,
    promise,
    dismiss,
    clear,
    pauseTimer,
    resumeTimer,
    withUndo
  }
}
